// Red Eye Tool — removes red-eye from photos.
// Click on the red-eye area to correct it.

import { PaintStrokeCommand, snapshotRegion } from "../commands/PaintStrokeCommand";
import { screenToDoc } from "../render/Viewport";
import { mergeBBox, type BBox } from "../pixel/brush";
import type { RasterLayer } from "../document/Layer";
import { findRaster } from "../document/LayerUtils";
import type { PointerSample, Tool, ToolContext } from "./Tool";

export class RedEyeTool implements Tool {
  readonly id = "red-eye";
  readonly shortcut = "J";

  /** Pupil darkness: how dark to make the corrected area (0-100). */
  pupilDarkness = 50;
  /** Brush size for the correction area. */
  size = 20;

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    if (!ctx.doc || !ctx.doc.activeLayerId) return;
    const layer = findRaster(ctx.doc.layers, ctx.doc.activeLayerId);
    if (!layer || !layer.visible) return;
    if (layer.lockAll || layer.lockImage) {
      ctx.showTip("Layer is locked.");
      return;
    }

    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const docX = p.x - layer.x, docY = p.y - layer.y;
    // Capture a full snapshot BEFORE mutation so undo can restore the original pixels.
    const preSnapshot = new Uint8ClampedArray(layer.pixels.data);
    const bbox = this.correctRedEye(layer, docX, docY);
    if (bbox) {
      const after = snapshotRegion(layer, bbox);
      const before = extractRegion(preSnapshot, layer.pixels.width, bbox);
      ctx.emit(new PaintStrokeCommand(layer.id, bbox, before, after));
    }
    ctx.requestRender();
  }

  private correctRedEye(layer: RasterLayer, cx: number, cy: number): BBox | null {
    const data = layer.pixels.data;
    const w = layer.pixels.width, h = layer.pixels.height;
    const radius = this.size / 2;
    const darkness = this.pupilDarkness / 100;

    const minX = Math.max(0, Math.floor(cx - radius));
    const minY = Math.max(0, Math.floor(cy - radius));
    const maxX = Math.min(w - 1, Math.ceil(cx + radius));
    const maxY = Math.min(h - 1, Math.ceil(cy + radius));
    const r2 = radius * radius;

    let touchX0 = w, touchY0 = h, touchX1 = -1, touchY1 = -1;

    for (let py = minY; py <= maxY; py++) {
      const dy = py + 0.5 - cy;
      for (let px = minX; px <= maxX; px++) {
        const dx = px + 0.5 - cx;
        if (dx * dx + dy * dy >= r2) continue;

        const idx = (py * w + px) * 4;
        const r = data[idx]!, g = data[idx + 1]!, b = data[idx + 2]!;

        // Detect red pixels: red channel significantly higher than green and blue
        if (r > 100 && r > g * 1.5 && r > b * 1.5) {
          // Desaturate and darken: convert to grayscale, then darken
          const gray = Math.round(r * 0.3 + g * 0.59 + b * 0.11);
          const factor = 1 - darkness * 0.5; // darken by up to 50%
          const newGray = Math.round(gray * factor);

          // Blend based on distance from center (soft edge)
          const dist = Math.sqrt(dx * dx + dy * dy) / radius;
          const blend = dist >= 1 ? 0 : 1 - dist * dist; // smooth falloff

          data[idx] = Math.round(r * (1 - blend) + newGray * blend);
          data[idx + 1] = Math.round(g * (1 - blend) + newGray * blend);
          data[idx + 2] = Math.round(b * (1 - blend) + newGray * blend);

          if (px < touchX0) touchX0 = px;
          if (py < touchY0) touchY0 = py;
          if (px > touchX1) touchX1 = px;
          if (py > touchY1) touchY1 = py;
        }
      }
    }

    if (touchX1 >= 0) {
      return { x: touchX0, y: touchY0, w: touchX1 - touchX0 + 1, h: touchY1 - touchY0 + 1 };
    }
    return null;
  }
}

function extractRegion(full: Uint8ClampedArray, fullWidth: number, bbox: BBox): Uint8ClampedArray {
  const out = new Uint8ClampedArray(bbox.w * bbox.h * 4);
  for (let row = 0; row < bbox.h; row++) {
    const srcOff = ((bbox.y + row) * fullWidth + bbox.x) * 4;
    const dstOff = row * bbox.w * 4;
    out.set(full.subarray(srcOff, srcOff + bbox.w * 4), dstOff);
  }
  return out;
}
